import { useState } from 'react';
import { HubSettingsSource } from '@/services/field.service';

/**
 * The operators we buy SIMs from — a shortcut, not the set of allowed values.
 * The backend stores whatever name it is given, so a hub carrying anything else
 * is typed in under "Other" and reads back exactly the same way.
 */
const KNOWN_OPERATORS = ['Orange', 'Objenious', 'Transatel'];

/** The modems we fit, on the same terms: a shortcut, not a closed set. */
const KNOWN_MODEMS = ['EG25G', 'EC25C', 'EC25EC'];

/** What the backend accepts: the digits printed on the card, nothing else. */
const ICCID_RE = /^\d{18,22}$/;

/** The 4G kit as the form holds it: ours to record, never read by the hub. */
export interface Mobile {
    operator: string;
    iccid: string;
    modem: string;
}

export function mobileOf(hub: HubSettingsSource): Mobile {
    return {
        operator: hub.simOperator ?? '',
        iccid: hub.simIccid ?? '',
        modem: hub.modem ?? '',
    };
}

/** The ICCID as stored: kept as typed in the field, spaces and all, since
 *  ICCIDs are printed in groups on the card and copied across as they read. */
export const iccidDigits = (iccid: string) => iccid.replace(/\s/g, '');

export const iccidValid = (iccid: string) => {
    const digits = iccidDigits(iccid);
    return digits === '' || ICCID_RE.test(digits);
};

/**
 * What the modem itself last said, as opposed to the notes below it: which
 * network it registered on and which APN it dialled. Read-only — nothing here
 * is ours to set — and blank on a hub whose firmware does not report it yet.
 * The signal strength that comes with it is on the hub card instead: it moves
 * from one wake-up to the next, which makes it something to glance at rather
 * than something to come in here and look up.
 */
function Reported({ hub }: { hub: HubSettingsSource }) {
    if (hub.modemOperator === null && hub.modemApn === null) return null;

    return (
        <div className="flex flex-col gap-1.5 rounded border border-[#1e242a] bg-[#0f141a] px-3 py-2">
            <span className="text-[10px] uppercase tracking-wider text-[#6b7480]">
                As reported by the modem
            </span>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <ReportedValue label="Network" value={hub.modemOperator} />
                <ReportedValue label="APN" value={hub.modemApn} />
            </div>
        </div>
    );
}

function ReportedValue({
    label,
    value,
}: {
    label: string;
    value: string | null;
}) {
    return (
        <span className="flex items-baseline gap-1.5">
            <span className="text-xs text-[#6b7480]">{label}</span>
            <span className="text-[#d8dde2]">
                {value ?? <span className="text-[#6b7480]">—</span>}
            </span>
        </span>
    );
}

/**
 * The SIM in the hub's modem and which modem that is: our own notes, beside
 * what the modem last reported, which is telemetry and read-only. The two are
 * worth reading together: a hub registered on a network other than the SIM we
 * recorded, or on none at all, is the first thing to look at when it goes
 * quiet. Saved with the rest of the dialog.
 */
export function MobileFields({
    hub,
    value,
    onChange,
    onEnter,
    onEscape,
}: {
    hub: HubSettingsSource;
    value: Mobile;
    onChange: (value: Mobile) => void;
    onEnter: () => void;
    onEscape: () => void;
}) {
    const valid = iccidValid(value.iccid);
    const digits = iccidDigits(value.iccid);

    return (
        <div className="flex flex-col gap-5">
            <Reported hub={hub} />
            <PickOrType
                label="Mobile network operator"
                known={KNOWN_OPERATORS}
                placeholder="Operator name"
                value={value.operator}
                onChange={(operator) => onChange({ ...value, operator })}
                onCommit={onEnter}
                onCancel={onEscape}
            />

            <div className="flex flex-col gap-1.5">
                <label htmlFor="sim-iccid" className="text-xs text-[#a8b0bb]">
                    ICCID
                </label>
                <input
                    id="sim-iccid"
                    value={value.iccid}
                    inputMode="numeric"
                    maxLength={30}
                    placeholder="8933..."
                    onChange={(event) =>
                        onChange({ ...value, iccid: event.target.value })
                    }
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') onEnter();
                        else if (event.key === 'Escape') onEscape();
                    }}
                    className={
                        'px-2 py-1.5 rounded text-sm font-mono tabular-nums bg-[#0f141a] border text-[#d8dde2] placeholder:text-[#6b7480] focus:outline-none ' +
                        (valid
                            ? 'border-[#2d2d2d] focus:border-[#74c69d]'
                            : 'border-[#d9534f]')
                    }
                />
                <p
                    className={
                        'text-[11px] ' +
                        (valid ? 'text-[#6b7480]' : 'text-[#ff8a84]')
                    }
                >
                    {valid
                        ? 'The number printed on the card — 18 to 22 digits. Spaces are ignored; leave empty if unknown.'
                        : `${digits.length} digits — an ICCID has 18 to 22.`}
                </p>
            </div>

            <PickOrType
                label="Modem"
                known={KNOWN_MODEMS}
                placeholder="Modem model"
                value={value.modem}
                onChange={(modem) => onChange({ ...value, modem })}
                onCommit={onEnter}
                onCancel={onEscape}
            />
        </div>
    );
}

/**
 * One value that is usually one of a few known names but need not be: a chip per
 * known name, plus "Other" to type anything else. Both the operator and the
 * modem work this way — the lists are what we buy and fit, not what is allowed.
 */
function PickOrType({
    label,
    known,
    placeholder,
    value,
    onChange,
    onCommit,
    onCancel,
}: {
    label: string;
    known: string[];
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    onCommit: () => void;
    onCancel: () => void;
}) {
    // Whether the value is being typed rather than picked. Starts on for a value
    // we have no chip for, so an existing one is never silently dropped by the
    // form that is meant to show it.
    const [custom, setCustom] = useState(
        value !== '' && !known.includes(value),
    );

    return (
        <div className="flex flex-col gap-2">
            <span className="text-xs text-[#a8b0bb]">{label}</span>
            <div className="flex flex-wrap gap-2">
                {known.map((name) => (
                    <Chip
                        key={name}
                        label={name}
                        selected={!custom && value === name}
                        onClick={() => {
                            setCustom(false);
                            // Pressing the value already set clears it, which is
                            // the only way back to "unknown".
                            onChange(value === name ? '' : name);
                        }}
                    />
                ))}
                <Chip
                    label="Other"
                    selected={custom}
                    onClick={() => {
                        setCustom(true);
                        if (known.includes(value)) onChange('');
                    }}
                />
            </div>
            {custom && (
                <input
                    autoFocus
                    value={value}
                    maxLength={40}
                    placeholder={placeholder}
                    onChange={(event) => onChange(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') onCommit();
                        else if (event.key === 'Escape') onCancel();
                    }}
                    className="px-2 py-1.5 rounded text-sm bg-[#0f141a] border border-[#2d2d2d] text-[#d8dde2] placeholder:text-[#6b7480] focus:outline-none focus:border-[#74c69d]"
                />
            )}
        </div>
    );
}

function Chip({
    label,
    selected,
    onClick,
}: {
    label: string;
    selected: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            className={
                'px-3 py-1 rounded-full text-xs border transition-colors ' +
                (selected
                    ? 'bg-[#1a2620] border-[#74c69d] text-[#74c69d]'
                    : 'bg-[#0f141a] border-[#2d2d2d] text-[#a8b0bb] hover:text-white hover:border-[#3a4249]')
            }
        >
            {label}
        </button>
    );
}
